import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Paper,
  Grid,
  TextField,
  Container,
  Stack,
  Chip,
  alpha,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import NotesIcon from "@mui/icons-material/Notes";
import InfoIcon from "@mui/icons-material/Info";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AsyncSelect from 'react-select/async';
import VariantSelector from '../../product/components/VariantSelector';
import IMEISerialDialog from '../../product/components/IMEISerialDialog';
import AddProductDialog from '../../product/components/AddProductDialog';
import { searchProducts, addProduct } from "../../product/services/productService";
import { addPurchaseInvoice } from "../services/purchaseService";
import { getSuppliers, searchSuppliersByName, addSupplier } from "../../suppliers/services/supplierService";
import { getVatRates } from "../../companysettings/services/vatService";
import SupplierSelect from "../../suppliers/components/SupplierSelect";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

function AddPurchasePage() {
  const navigate = useNavigate();
  
  // Get VAT rates from Redux store
  const vatRatesFromStore = useSelector((state) => state.vat?.vatRates || []);
  const [vatRates, setVatRates] = useState([]);
  
  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({ 
    Name: "", 
    SKU: "", 
    SalePrice: "", 
    PurchasePrice: "" 
  });
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState("");
  const [taxRate, setTaxRate] = useState(21); // Default 21% VAT
  
  // Invoice header state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  
  // Line items
  const [lineItems, setLineItems] = useState([]);
  
  // Auto-generated invoice number
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    const year = new Date().getFullYear();
    const timestamp = Date.now();
    return `PI-${year}-${String(timestamp % 10000).padStart(4, "0")}`;
  });

  // Flags
  const [updateInventory, setUpdateInventory] = useState(true);
  const [updateCostPrices, setUpdateCostPrices] = useState(true);
  const [isICA, setIsICA] = useState(false); // ICA Purchases flag (critical - default OFF)
  const [isPriceIncludeVAT, setIsPriceIncludeVAT] = useState(true); // Price includes VAT (default ON)

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  // Variant + IMEI state
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [imeiDialogOpen, setImeiDialogOpen] = useState(false);
  const [pendingImeiItems, setPendingImeiItems] = useState([]);
  
  // ✅ Computed: Determine if IMEI/Serial entry is required
  const requiresSerialNumbers = React.useMemo(() => {
    // Case 1: Variant is selected and is serialized
    if (selectedVariant?.isSerializedProduct) {
      return true;
    }
    // Case 2: Product without variant but is serialized
    if (!selectedVariant && selectedProduct?.IsSerializedProduct) {
      return true;
    }
    return false;
  }, [selectedVariant, selectedProduct]);
  
  // ✅ Dialog states
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', contactPerson: '', email: '', phone: '', address: '' });
  const [supplierFormTouched, setSupplierFormTouched] = useState(false);
  
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productFormTouched, setProductFormTouched] = useState(false);
  
  // ✅ File upload state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceFileName, setInvoiceFileName] = useState('');

  // ✅ Auto-calculate totals
  const calculations = React.useMemo(() => {
    const subTotal = lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
    
    // Calculate tax based on whether price includes VAT
    const taxAmount = lineItems.reduce((sum, item) => {
      let itemTax;
      if (isPriceIncludeVAT) {
        // VAT is included - extract it from the price
        itemTax = (item.totalPrice * item.taxRate) / (100 + item.taxRate);
      } else {
        // VAT not included - add it on top
        itemTax = (item.totalPrice * item.taxRate) / 100;
      }
      return sum + itemTax;
    }, 0);
    
    const totalAmount = isPriceIncludeVAT 
      ? subTotal - discountAmount  // VAT already included in subTotal
      : subTotal + taxAmount - discountAmount;  // Add VAT on top
    
    return {
      subTotal: subTotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      discountAmount: Number(discountAmount).toFixed(2),
      totalAmount: totalAmount.toFixed(2)
    };
  }, [lineItems, discountAmount, isPriceIncludeVAT]);

  // Fetch suppliers and VAT rates on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      try {
        const response = await getSuppliers();
        const suppliersData = response?.payload || response || [];
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
        setSuppliers([]);
      } finally {
        setSuppliersLoading(false);
      }
    };
    
    const fetchVatRates = async () => {
      // ✅ Use VAT rates from Redux if available, otherwise fetch from API
      if (vatRatesFromStore && vatRatesFromStore.length > 0) {
        setVatRates(vatRatesFromStore);
      } else {
        try {
          const response = await getVatRates();
          const rates = response?.payload || response || [];
          setVatRates(Array.isArray(rates) ? rates : []);
        } catch (error) {
          console.error('Error fetching VAT rates:', error);
          // Use default rates if API fails
          setVatRates([]);
        }
      }
    };
    
    fetchSuppliers();
    fetchVatRates();
  }, []);

  // Auto-set due date (30 days from invoice date)
  useEffect(() => {
    if (invoiceDate && !dueDate) {
      const invoice = new Date(invoiceDate);
      const due = new Date(invoice);
      due.setDate(due.getDate() + 30);
      setDueDate(due.toISOString().slice(0, 10));
    }
  }, [invoiceDate]);

  // ✅ Debounced product search to prevent excessive API calls
  const loadProductOptions = React.useCallback((inputValue) => {
    return new Promise((resolve) => {
      if (!inputValue || inputValue.length < 2) {
        resolve([]);
        return;
      }

      // Clear previous timeout
      if (loadProductOptions.timeoutId) {
        clearTimeout(loadProductOptions.timeoutId);
      }

      // Set new timeout for debouncing (500ms)
      loadProductOptions.timeoutId = setTimeout(async () => {
        try {
          let paramsObj;
          if (/^\d+$/.test(inputValue)) {
            paramsObj = { id: inputValue, sku: inputValue };
          } else {
            paramsObj = { name: inputValue, sku: inputValue };
          }
          const data = await searchProducts(paramsObj);
          const productsArray = Array.isArray(data?.payload) ? data.payload : Array.isArray(data) ? data : [];
          const options = productsArray.map((p) => ({
            value: p.Id,
            label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}`,
            ...p,
          }));
          // ✅ Add "+ Add New Product" option after API response
          options.push({ value: "new", label: "+ Add New Product" });
          resolve(options);
        } catch (error) {
          console.error('Error searching products:', error);
          resolve([]);
        }
      }, 500);
    });
  }, []);

  const loadSupplierOptions = async (inputValue) => {
    try {
      if (!inputValue || inputValue.trim().length < 3) return [];
      const response = await searchSuppliersByName(inputValue.trim());
      const searchResults = response?.payload || response || [];
      
      if (Array.isArray(searchResults)) {
        return searchResults.map(s => ({
          value: s.Id,
          label: s.Name,
          Name: s.Name,
          ...s
        }));
      }
      return [];
    } catch (error) {
      console.error('Error searching suppliers:', error);
      // ✅ Don't show toast here - let the SupplierSelect component handle it with debouncing
      return [];
    }
  };

  const handleProductSelect = (product) => {
    if (product && product.value === "new") {
      // Open product dialog instead of inline form
      setOpenProductDialog(true);
      setSelectedProduct(null);
    } else {
      setIsNewProduct(false);
      setSelectedProduct(product);
      // ✅ Clear unit price when product is selected
      setUnitPrice("");
      // Reset variant + IMEI when product changes
      setSelectedVariant(null);
      setPendingImeiItems([]);
      
      // Log serialization status for debugging
      console.log('📦 Product selected:', {
        name: product?.Name,
        hasVariants: product?.HasVariants || false,
        isSerializedProduct: product?.IsSerializedProduct || false
      });
    }
  };

  // ✅ Updated handleAddLineItem - merge products+variants if already exists
  const handleAddLineItem = async () => {
    let productId = selectedProduct?.Id || selectedProduct?.value;
    let productName = selectedProduct?.Name || newProductData.Name;
    let sku = selectedProduct?.SKU || newProductData.SKU;

    if (isNewProduct) {
      try {
        const response = await addProduct(newProductData);
        const newProd = response?.payload || response;
        productId = newProd.Id;
        productName = newProd.Name;
        sku = newProd.SKU;
      } catch (error) {
        console.error('Error adding new product:', error);
        toast.error('Failed to add new product');
        return;
      }
    }

    if (!productId || !quantity || !unitPrice) {
      toast.error('Please fill all product fields');
      return;
    }

    // ✅ Validate IMEI/Serial numbers for serialized products
    const isSerializedItem = selectedVariant?.isSerializedProduct || selectedProduct?.IsSerializedProduct;
    
    if (isSerializedItem) {
      const expectedCount = Number(quantity);
      const enteredCount = pendingImeiItems.length;
      
      if (enteredCount === 0) {
        const confirm = window.confirm(
          `⚠️ This ${selectedVariant ? 'variant' : 'product'} requires IMEI/Serial numbers.\n\n` +
          `You need to enter ${expectedCount} IMEI/Serial number(s).\n\n` +
          `Do you want to add them now? (Click Cancel to add without IMEI - can be added later)`
        );
        
        if (confirm) {
          setImeiDialogOpen(true);
          return;
        }
        // User chose to proceed without IMEI
        toast.warning('Item added without IMEI/Serial numbers. You can add them later.');
      } else if (enteredCount !== expectedCount) {
        toast.error(
          `IMEI/Serial number count mismatch!\n` +
          `Expected: ${expectedCount}, Entered: ${enteredCount}\n` +
          `Please enter exactly ${expectedCount} IMEI/Serial number(s).`
        );
        setImeiDialogOpen(true);
        return;
      }
    }

    const variantId = selectedVariant?.id || null;
    const variantName = selectedVariant?.variantName || null;

    // ✅ Check if same product+variant already exists in line items
    const existingItemIndex = lineItems.findIndex(
      item => item.productId === productId && item.variantId === variantId
    );

    if (existingItemIndex !== -1) {
      // ✅ Merge - update quantity and append IMEI items
      setLineItems(prev => {
        const updated = [...prev];
        const existingItem = updated[existingItemIndex];
        const newQuantity = existingItem.quantity + Number(quantity);
        const newTotalPrice = newQuantity * Number(unitPrice);
        
        // Merge IMEI items
        const mergedImeiItems = [...(existingItem.imeiItems || []), ...pendingImeiItems];
        
        updated[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          unitPrice: Number(unitPrice),
          taxRate: Number(taxRate),
          totalPrice: newTotalPrice,
          imeiItems: mergedImeiItems,
        };
        return updated;
      });
      
      const variantDisplay = variantName ? ` • ${variantName}` : '';
      toast.success(
        `Quantity updated for ${productName}${variantDisplay}\n` +
        (pendingImeiItems.length > 0 ? `Added ${pendingImeiItems.length} IMEI/Serial numbers` : '')
      );
    } else {
      // ✅ New line item
      const lineItem = {
        productId,
        productName,
        sku,
        variantId,
        variantName,
        hasVariants: selectedProduct?.HasVariants || false,
        isSerializedProduct: isSerializedItem,
        imeiItems: pendingImeiItems,
        quantity: Number(quantity),
        unitPrice: Number(unitPrice),
        taxRate: Number(taxRate),
        totalPrice: Number(quantity) * Number(unitPrice),
      };
      
      setLineItems(prev => [...prev, lineItem]);
      
      const variantDisplay = variantName ? ` • ${variantName}` : '';
      const imeiDisplay = pendingImeiItems.length > 0 ? ` (${pendingImeiItems.length} IMEI/Serial)` : '';
      toast.success(`Line item added: ${productName}${variantDisplay}${imeiDisplay}`);
    }

    // Reset product fields
    setSelectedProduct(null);
    setIsNewProduct(false);
    setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
    setQuantity(1);
    setUnitPrice("");
    setTaxRate(21);
    setSelectedVariant(null);
    setPendingImeiItems([]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
    toast.info('Line item removed');
  };
  // ✅ Handle Add Supplier
  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      setSupplierFormTouched(true);
      toast.error('Supplier name is required');
      return;
    }

    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };

      const response = await addSupplier(supplierData);
      toast.success('Supplier added successfully');
      
      // Refresh suppliers list
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      }
      
      // Set the newly added supplier as selected
      const addedSupplierId = response.Id || response.id || response.payload?.Id;
      if (addedSupplierId) {
        setSelectedSupplier({
          value: addedSupplierId,
          label: newSupplier.name.trim()
        });
      }
      
      setOpenSupplierDialog(false);
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setSupplierFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding supplier:', error);
      toast.error(error.response?.data?.message || 'Failed to add supplier');
    }
  };

  // ✅ Handle Add Product
  const handleAddProductFromDialog = async () => {
    if (!newProductData.Name.trim()) {
      setProductFormTouched(true);
      toast.error('Product name is required');
      return;
    }

    try {
      const productData = {
        Name: newProductData.Name.trim(),
        SKU: newProductData.SKU?.trim() || null,
        SalePrice: parseFloat(newProductData.SalePrice) || 0,
        PurchasePrice: parseFloat(newProductData.PurchasePrice) || 0,
        Quantity: 0,
        Description: null,
        CategoryId: null
      };

      const response = await addProduct(productData);
      toast.success('Product added successfully');
      
      // Set the newly added product as selected
      const addedProductId = response.Id || response.id || response.payload?.Id;
      if (addedProductId) {
        setSelectedProduct({
          value: addedProductId,
          label: newProductData.Name.trim(),
          sku: newProductData.SKU?.trim() || ''
        });
        setUnitPrice(newProductData.PurchasePrice || '');
      }
      
      setOpenProductDialog(false);
      setNewProductData({ Name: "", SKU: "", SalePrice: "", PurchasePrice: "" });
      setProductFormTouched(false);

    } catch (error) {
      console.error('❌ Error adding product:', error);
      toast.error(error.response?.data?.message || 'Failed to add product');
    }
  };

  // ✅ Handle File Upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (PDF, images)
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setInvoiceFile(file);
      setInvoiceFileName(file.name);
      toast.success(`File ${file.name} uploaded successfully`);
    }
  };
  const handleSubmitPurchaseInvoice = async () => {
    // Validation
    if (!selectedSupplier) {
      toast.error('Please select a supplier');
      return;
    }
    
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    if (!invoiceDate) {
      toast.error('Please select an invoice date');
      return;
    }

    if (!invoiceNumber || !invoiceNumber.trim()) {
      toast.error('Invoice number is required');
      return;
    }

    // ✅ Prepare form data with individual fields (backend expects [FromForm])
    const formData = new FormData();
    
    // ✅ Append header fields directly
    formData.append('PurchaseInvoiceNumber', invoiceNumber.trim());
    formData.append('InvoiceDate', invoiceDate);
    if (dueDate) formData.append('DueDate', dueDate);
    formData.append('SupplierId', selectedSupplier.value);
    formData.append('Status', 'Not Paid'); // 1 = Not Paid/Pending
    formData.append('SubTotal', parseFloat(calculations.subTotal));
    formData.append('TaxAmount', parseFloat(calculations.taxAmount));
    formData.append('DiscountAmount', parseFloat(calculations.discountAmount));
    formData.append('TotalAmount', parseFloat(calculations.totalAmount));
    if (notes) formData.append('Notes', notes);
    if (referenceNumber) formData.append('ReferenceNumber', referenceNumber);
    formData.append('UpdateInventory', updateInventory);
    formData.append('UpdateCostPrices', updateCostPrices);
    formData.append('IsICA', isICA);
    
    // ✅ Append line items with indexed notation for [FromForm] binding
    lineItems.forEach((item, index) => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = (subtotal * (item.discountPercentage || 0)) / 100;
      const subtotalAfterDiscount = subtotal - discountAmount;
      
      // Calculate tax based on whether price includes VAT
      let taxAmount, lineTotal;
      if (isPriceIncludeVAT) {
        taxAmount = (subtotalAfterDiscount * (item.taxRate || 0)) / (100 + (item.taxRate || 0));
        lineTotal = subtotalAfterDiscount;
      } else {
        taxAmount = (subtotalAfterDiscount * (item.taxRate || 0)) / 100;
        lineTotal = subtotalAfterDiscount + taxAmount;
      }
      
      formData.append(`LineItems[${index}].ProductId`, item.productId);
      if (item.variantId) formData.append(`LineItems[${index}].ProductVariantId`, item.variantId);
      formData.append(`LineItems[${index}].HasVariants`, item.hasVariants || false);
      formData.append(`LineItems[${index}].IsSerializedProduct`, item.isSerializedProduct || false);
      formData.append(`LineItems[${index}].Quantity`, item.quantity);
      formData.append(`LineItems[${index}].UnitCost`, item.unitPrice);
      formData.append(`LineItems[${index}].DiscountPercentage`, item.discountPercentage || 0);
      formData.append(`LineItems[${index}].DiscountAmount`, discountAmount);
      formData.append(`LineItems[${index}].TaxPercentage`, item.taxRate || 0);
      formData.append(`LineItems[${index}].TaxAmount`, taxAmount);
      formData.append(`LineItems[${index}].LineTotal`, lineTotal);
      if (item.description) formData.append(`LineItems[${index}].Description`, item.description);
      
      // ✅ Append IMEI/Serial inventory items with nested indexed notation
      if (item.isSerializedProduct && item.imeiItems?.length > 0) {
        item.imeiItems.forEach((imeiItem, imeiIndex) => {
          if (imeiItem.imei) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].IMEI`, imeiItem.imei);
          }
          if (imeiItem.serialNumber) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].SerialNumber`, imeiItem.serialNumber);
          }
          if (imeiItem.warrantyExpiryDate) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].WarrantyExpiryDate`, imeiItem.warrantyExpiryDate);
          }
          if (imeiItem.notes) {
            formData.append(`LineItems[${index}].InventoryItems[${imeiIndex}].Notes`, imeiItem.notes);
          }
        });
      }
    });
    
    // ✅ Add invoice PDF file(s) if uploaded
    if (invoiceFile) {
      formData.append('InvoicePDF', invoiceFile);
    }

    try {
      // ✅ Send formData (backend expects [FromForm])
      const response = await addPurchaseInvoice(formData);
      toast.success('Purchase invoice created successfully!');
      navigate('/purchases');
    } catch (error) {
      console.error('❌ Error creating purchase invoice:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create purchase invoice');
      }
    }
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                    <ReceiptIcon sx={{ color: "#1976d2" }} fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      New Purchase Invoice
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create and manage purchase orders
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={invoiceNumber}
                  color="primary"
                  sx={{ fontWeight: 'bold', fontSize: '1em', borderRadius: '8px', px: 2 }}
                />
              </Box>
            }
            sx={{
              background: alpha("#1976d2", 0.08),
              borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
            }}
          />
          <CardContent>
            <Grid container spacing={3}>
              {/* Supplier Selection */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon sx={{ color: "#1976d2" }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Supplier Information *
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenSupplierDialog(true)}
                      sx={{ borderRadius: 1.5, textTransform: 'none' }}
                    >
                      Add New
                    </Button>
                  </Stack>
                  <SupplierSelect
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    loadOptions={loadSupplierOptions}
                    isLoading={suppliersLoading}
                    placeholder="Type at least 3 characters to search..."
                  />
                </Paper>
              </Grid>

              {/* Invoice Details */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}`, height: '100%' }}>
                  <Stack direction="row" alignments="center" spacing={1} mb={2}>
                    <CalendarTodayIcon sx={{ color: "#1976d2" }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Invoice Details
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Invoice Date *"
                        type="date"
                        value={invoiceDate}
                        onChange={e => setInvoiceDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Due Date"
                        type="date"
                        value={dueDate}
                        onChange={e => setDueDate(e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Reference Number (Supplier's Invoice)"
                        value={referenceNumber}
                        onChange={e => setReferenceNumber(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., SUP-2024-001"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<UploadFileIcon />}
                          fullWidth
                          sx={{ borderRadius: 1.5, justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          {invoiceFileName || 'Upload Invoice (PDF/Image)'}
                          <input
                            type="file"
                            hidden
                            accept="application/pdf,image/*"
                            onChange={handleFileUpload}
                          />
                        </Button>
                        {invoiceFileName && (
                          <Chip
                            label={invoiceFileName}
                            onDelete={() => {
                              setInvoiceFile(null);
                              setInvoiceFileName('');
                            }}
                            color="primary"
                            size="small"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Add Product Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Add Products
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: isPriceIncludeVAT ? '#4caf50' : '#ff9800',
                      bgcolor: isPriceIncludeVAT ? '#e8f5e9' : '#fff3e0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: isPriceIncludeVAT ? '#2e7d32' : '#f57c00'
                      }}
                    >
                      Price Includes VAT
                    </Typography>
                    <Switch
                      checked={isPriceIncludeVAT}
                      onChange={(e) => setIsPriceIncludeVAT(e.target.checked)}
                      color="success"
                      size="small"
                    />
                  </Paper>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenProductDialog(true)}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Add New Product
                  </Button>
                </Box>
              </Box>
            }
            sx={{
              background: alpha("#4caf50", 0.08),
              borderBottom: `1px solid ${alpha("#4caf50", 0.15)}`
            }}
          />
          <CardContent>
            <Grid container spacing={2} alignItems="flex-start">
              {/* ✅ Product Select - Full width on its own row */}
              <Grid item xs={12}>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadProductOptions}
                  value={selectedProduct}
                  onChange={handleProductSelect}
                  placeholder="Search product by name, SKU, or ID..."
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      minHeight: '45px',
                      borderColor: alpha("#1976d2", 0.3),
                      '&:hover': {
                        borderColor: "#1976d2"
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '12px',
                      zIndex: 9999
                    })
                  }}
                />
              </Grid>

              {/* ── Variant Selector (shown only when product has variants) ── */}
              {selectedProduct && selectedProduct.HasVariants && (
                <>
                  <Grid item xs={12}>
                    <VariantSelector
                      productId={selectedProduct?.Id || selectedProduct?.value}
                      value={selectedVariant}
                      onChange={(v) => {
                        setSelectedVariant(v);
                        if (v?.costPrice) setUnitPrice(String(v.costPrice));
                        setPendingImeiItems([]);
                      }}
                      label="Select Variant (optional)"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      variant="text"
                      color="secondary"
                      onClick={() => {
                        toast.info('Add Variant feature: Navigate to product detail page to add variants');
                        // You can implement navigation to product detail page here
                        // navigate(`/products/${selectedProduct.Id || selectedProduct.value}`);
                      }}
                      sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                    >
                      + Add Variant for this Product
                    </Button>
                  </Grid>
                </>
              )}

               {/* Quantity, Unit Price, Tax Rate, Add to Line Items */}
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Quantity *"
                  type="number"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, Number(e.target.value)))}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  disabled={requiresSerialNumbers && pendingImeiItems.length > 0}
                  helperText={requiresSerialNumbers && pendingImeiItems.length > 0 ? "Auto-synced with IMEI count" : ""}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Unit Price *"
                  type="number"
                  value={unitPrice}
                  onChange={e => setUnitPrice(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tax Rate (%)</InputLabel>
                  <Select
                    value={taxRate}
                    onChange={e => setTaxRate(e.target.value)}
                    label="Tax Rate (%)"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, borderRadius: 1.5 }}
                  >
                    {vatRates && vatRates.length > 0 ? (
                      vatRates.map((rate) => (
                        <MenuItem key={rate.Id || rate.id} value={rate.VATPercentage}>
                          {rate.VATPercentage}% {rate.Name ? `- ${rate.Name}` : ''}
                        </MenuItem>
                      ))
                    ) : (
                      [
                        
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  fullWidth
                  onClick={handleAddLineItem}
                  disabled={!selectedProduct || !quantity || !unitPrice}
                  sx={{
                    height: '40px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
              {/* ── IMEI / Serial entry (shown for serialized products/variants, AFTER quantity is set) ── */}
              {requiresSerialNumbers && selectedProduct && (
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      border: '2px solid',
                      borderColor: pendingImeiItems.length === Number(quantity) && pendingImeiItems.length > 0
                        ? 'success.main' 
                        : pendingImeiItems.length > 0
                        ? 'warning.main'
                        : 'info.main',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: pendingImeiItems.length === Number(quantity) && pendingImeiItems.length > 0
                        ? alpha('#4caf50', 0.06) 
                        : pendingImeiItems.length > 0
                        ? alpha('#ff9800', 0.06)
                        : alpha('#2196f3', 0.06),
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          🔒 IMEI / Serial Numbers Required
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedVariant ? (
                            <>
                              Purchasing <strong>{quantity}</strong> unit(s) of <strong>{selectedVariant.variantName}</strong>
                            </>
                          ) : (
                            <>
                              Purchasing <strong>{quantity}</strong> unit(s) of <strong>{selectedProduct.Name}</strong> (serialized product)
                            </>
                          )}
                          {' '}— enter one IMEI/Serial per unit
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          icon={<QrCodeIcon sx={{ fontSize: 14 }} />}
                          label={
                            pendingImeiItems.length === Number(quantity) && pendingImeiItems.length > 0
                              ? `${pendingImeiItems.length} / ${quantity} ✔ Complete`
                              : pendingImeiItems.length > Number(quantity)
                              ? `${pendingImeiItems.length} / ${quantity} ⚠️ Too many`
                              : pendingImeiItems.length > 0
                              ? `${pendingImeiItems.length} / ${quantity} ⚠️ Incomplete`
                              : `0 / ${quantity} - Not entered`
                          }
                          color={
                            pendingImeiItems.length === Number(quantity) && pendingImeiItems.length > 0
                              ? 'success'
                              : pendingImeiItems.length > 0
                              ? 'warning'
                              : 'default'
                          }
                          size="small"
                          variant="outlined"
                        />
                        <Button
                          variant="contained"
                          color={pendingImeiItems.length === Number(quantity) && pendingImeiItems.length > 0 ? 'success' : 'info'}
                          size="small"
                          startIcon={<QrCodeIcon />}
                          onClick={() => setImeiDialogOpen(true)}
                          sx={{ borderRadius: 1.5, textTransform: 'none', minWidth: 120 }}
                        >
                          {pendingImeiItems.length > 0 ? 'Edit IMEIs' : 'Enter IMEIs'}
                        </Button>
                      </Stack>
                    </Stack>
                    {/* Compact preview of entered IMEIs */}
                    {pendingImeiItems.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {pendingImeiItems.slice(0, 10).map((item, i) => (
                          <Chip
                            key={i}
                            icon={<QrCodeIcon sx={{ fontSize: 12 }} />}
                            label={item.imei || item.serialNumber || `Item ${i + 1}`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        ))}
                        {pendingImeiItems.length > 10 && (
                          <Chip
                            label={`+${pendingImeiItems.length - 10} more`}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

            {isNewProduct && (
              <Box sx={{ mt: 3, p: 2, bgcolor: alpha("#ff9800", 0.05), borderRadius: 2, border: `1px dashed ${alpha("#ff9800", 0.3)}` }}>
                <Typography variant="subtitle2" fontWeight="bold" mb={2} color="#ff9800">
                  Add New Product
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Product Name *"
                      value={newProductData.Name}
                      onChange={e => setNewProductData({ ...newProductData, Name: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SKU *"
                      value={newProductData.SKU}
                      onChange={e => setNewProductData({ ...newProductData, SKU: e.target.value })}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Sale Price"
                      type="number"
                      value={newProductData.SalePrice}
                      onChange={e => setNewProductData({ ...newProductData, SalePrice: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Purchase Price"
                      type="number"
                      value={newProductData.PurchasePrice}
                      onChange={e => setNewProductData({ ...newProductData, PurchasePrice: e.target.value })}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Line Items Table */}
        {lineItems.length > 0 && (
          <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InventoryIcon sx={{ color: "#1976d2", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Line Items
                    </Typography>
                    <Chip
                      label={`${lineItems.length} item${lineItems.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>
              }
              sx={{
                background: alpha("#1976d2", 0.08),
                borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha("#1976d2", 0.05) }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product / Variant</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>IMEI</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          {item.productName}
                          {item.variantName && (
                            <Typography variant="caption" display="block" color="primary.main" fontWeight={500}>
                              {item.variantName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell>
                          {item.isSerializedProduct ? (
                            <Chip
                              label={`${item.imeiItems?.length || 0} IMEI`}
                              size="small"
                              color={item.imeiItems?.length > 0 ? 'success' : 'warning'}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const newQty = parseFloat(e.target.value) || 0;
                              if (newQty > 0) {
                                setLineItems(prev => {
                                  const updated = [...prev];
                                  updated[idx] = {
                                    ...updated[idx],
                                    quantity: newQty,
                                    totalPrice: newQty * updated[idx].unitPrice
                                  };
                                  return updated;
                                });
                              }
                            }}
                            sx={{ width: '80px' }}
                            inputProps={{ min: 1, step: 1 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => {
                              const newPrice = parseFloat(e.target.value) || 0;
                              if (newPrice >= 0) {
                                setLineItems(prev => {
                                  const updated = [...prev];
                                  updated[idx] = {
                                    ...updated[idx],
                                    unitPrice: newPrice,
                                    totalPrice: updated[idx].quantity * newPrice
                                  };
                                  return updated;
                                });
                              }
                            }}
                            sx={{ width: '100px' }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>,
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell align="right">{item.taxRate}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          €{item.totalPrice.toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLineItem(idx)}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              {/* Totals Section */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {/* Notes and Discount */}
                    <Stack spacing={2}>
                      <TextField
                        label="Discount Amount"
                        type="number"
                        value={discountAmount}
                        onChange={e => setDiscountAmount(Math.max(0, Number(e.target.value)))}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><DiscountIcon fontSize="small" /></InputAdornment>,
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      <TextField
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        placeholder="Add any additional notes..."
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><NotesIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                      
                      {/* ICA Purchases - Critical Setting */}
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          bgcolor: isICA ? '#fff3e0' : '#f5f5f5',
                          border: isICA ? '2px solid #ff9800' : '2px solid #e0e0e0',
                          borderRadius: 2,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={700} color={isICA ? 'warning.main' : 'text.secondary'}>
                                ⚠️ ICA Purchases
                              </Typography>
                            </Box>
                            <Switch
                              checked={isICA}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const confirmed = window.confirm(
                                    'WARNING: ICA Purchases is a critical setting.\\n\\nEnabling this will affect accounting and tax calculations.\\n\\nAre you sure you want to enable ICA Purchases?'
                                  );
                                  if (confirmed) {
                                    setIsICA(true);
                                  }
                                } else {
                                  setIsICA(false);
                                }
                              }}
                              size="small"
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#ff9800',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#ff9800',
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Critical setting - affects accounting & tax calculations
                          </Typography>
                          {isICA && (
                            <Box sx={{ 
                              bgcolor: '#fff3e0', 
                              p: 1, 
                              borderRadius: 1, 
                              border: '1px solid #ff9800',
                              mt: 0.5
                            }}>
                              <Typography variant="caption" color="warning.dark" fontWeight={600}>
                                ⚠️ ICA Purchases mode is ACTIVE
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#1976d2", 0.05) }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Subtotal:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.subTotal}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Tax Amount:</Typography>
                          <Typography variant="body1" fontWeight="medium">€{calculations.taxAmount}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1">Discount:</Typography>
                          <Typography variant="body1" fontWeight="medium" color="error.main">
                            -€{calculations.discountAmount}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            Total Amount:
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            €{calculations.totalAmount}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Settings and Submit */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: alpha("#4caf50", 0.05) }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateInventory}
                            onChange={e => setUpdateInventory(e.target.checked)}
                            color="success"
                          />
                        }
                        label="Update Inventory Stock"
                      />
                      <Tooltip title="Automatically update product stock quantities">
                        <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={updateCostPrices}
                            onChange={e => setUpdateCostPrices(e.target.checked)}
                            color="success"
                          />
                        }
                        label="Update Product Cost Prices"
                      />
                      <Tooltip title="Update product cost prices based on this purchase">
                        <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                      </Tooltip>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/purchases')}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 4
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handleSubmitPurchaseInvoice}
                    disabled={!selectedSupplier || lineItems.length === 0}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      px: 4
                    }}
                  >
                    Create Purchase Invoice
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── IMEI Entry Dialog ── */}
        <IMEISerialDialog
          open={imeiDialogOpen}
          onClose={() => setImeiDialogOpen(false)}
          quantity={Number(quantity)}
          variantName={selectedVariant?.variantName || selectedProduct?.Name || 'Product'}
          variantId={selectedVariant?.id || null}
          productId={selectedProduct?.Id || selectedProduct?.value}
          existingItems={pendingImeiItems}
          onSave={(items) => {
            setPendingImeiItems(items);
            const prevQuantity = Number(quantity);
            const newQuantity = items.length;
            setQuantity(newQuantity); // ✅ Auto-sync quantity with IMEI count
            
            // Notify user about auto-sync
            if (prevQuantity !== newQuantity) {
              toast.info(`Quantity automatically updated from ${prevQuantity} to ${newQuantity} to match IMEI count`);
            }
            
            setImeiDialogOpen(false);
          }}
        />

        {/* ✅ Supplier Dialog */}
        <Dialog 
          open={openSupplierDialog} 
          onClose={() => {
            setOpenSupplierDialog(false);
            setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
            setSupplierFormTouched(false);
          }} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add New Supplier</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Supplier Name *"
              fullWidth
              required
              value={newSupplier.name}
              onChange={(e) => {
                setNewSupplier({ ...newSupplier, name: e.target.value });
                if (supplierFormTouched && e.target.value.trim()) {
                  setSupplierFormTouched(false);
                }
              }}
              onBlur={() => {
                if (!newSupplier.name.trim()) {
                  setSupplierFormTouched(true);
                }
              }}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText={supplierFormTouched && !newSupplier.name.trim() ? "Supplier name is required" : "Required field"}
              error={supplierFormTouched && !newSupplier.name.trim()}
            />
            <TextField
              margin="dense"
              label="Contact Person"
              fullWidth
              value={newSupplier.contactPerson || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={newSupplier.email || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Phone"
              fullWidth
              value={newSupplier.phone || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
            <TextField
              margin="dense"
              label="Address"
              fullWidth
              multiline
              rows={2}
              value={newSupplier.address || ''}
              onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
              sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              helperText="Optional"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => {
                setOpenSupplierDialog(false);
                setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
                setSupplierFormTouched(false);
              }}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddSupplier} 
              variant="contained" 
              color="primary"
              sx={{ borderRadius: 1.5 }}
              disabled={!newSupplier.name.trim()}
            >
              Add Supplier
            </Button>
          </DialogActions>
        </Dialog>

        {/* ✅ AddProductDialog for consistency */}
        <AddProductDialog
          open={openProductDialog}
          onClose={() => setOpenProductDialog(false)}
          onProductCreated={(newProduct) => {
            // Set the newly added product as selected
            setSelectedProduct({
              value: newProduct.Id || newProduct.id,
              label: newProduct.Name,
              Name: newProduct.Name,
              SKU: newProduct.SKU,
              ...newProduct
            });
            setOpenProductDialog(false);
            toast.success('Product added successfully');
          }}
        />
      </Container>
    </Box>
  );
}

export default AddPurchasePage;